import { Injectable } from '@angular/core';

const MUTED_KEY = 'pcg_voice_muted';

/**
 * Speaks the conversation's questions out loud (plan: "Plan it with me").
 *
 * For now this is the browser's own speech synthesis, so the voice varies by
 * device. The fixed questions are meant to become pre-recorded clips in a
 * better voice; this service is the one place that would change.
 *
 * Everything spoken is also shown as a caption, so muting loses nothing.
 * iOS only lets a page speak after a tap: the first speak() must run inside
 * the tap that starts the conversation, before any await.
 */
@Injectable({ providedIn: 'root' })
export class SpeakerService {
  private token = 0;

  get supported(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';
  }

  get muted(): boolean {
    try { return localStorage.getItem(MUTED_KEY) === '1'; } catch { return false; }
  }

  set muted(value: boolean) {
    try { value ? localStorage.setItem(MUTED_KEY, '1') : localStorage.removeItem(MUTED_KEY); } catch { /* private mode */ }
    if (value) this.cancel();
  }

  /** Resolves when the line has been spoken — or straight away when muted or unsupported. */
  speak(text: string): Promise<void> {
    if (this.muted || !this.supported) return Promise.resolve();
    this.cancel();
    const mine = ++this.token;

    return new Promise(resolve => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(guard);
        resolve();
      };
      // Some browsers never fire onend; don't leave the conversation waiting.
      const guard = setTimeout(done, 1500 + text.length * 80);

      try {
        const u = new SpeechSynthesisUtterance(text.replace(/[—–]/g, ','));
        const voice = this.pickVoice();
        if (voice) u.voice = voice;
        u.lang = voice?.lang || navigator.language || 'en-US';
        u.rate = 1.03;
        u.pitch = 1.05;
        u.onend = () => { if (mine === this.token) done(); };
        u.onerror = () => done();
        window.speechSynthesis.speak(u);
      } catch {
        done();
      }
    });
  }

  cancel(): void {
    this.token++;
    try { if (this.supported) window.speechSynthesis.cancel(); } catch { /* nothing to stop */ }
  }

  /** The friendliest English voice this device has, if any. */
  private pickVoice(): SpeechSynthesisVoice | null {
    const voices = window.speechSynthesis.getVoices() || [];
    const english = voices.filter(v => /^en/i.test(v.lang));
    const preferred = /Natural|Neural|Google UK English Female|Samantha|Karen|Moira|Tessa|Serena|Aria|Jenny|Google US English/i;
    const local = (navigator.language || '').toLowerCase();
    return english.find(v => preferred.test(v.name) && v.lang.toLowerCase() === local)
      || english.find(v => preferred.test(v.name))
      || english.find(v => v.lang.toLowerCase() === local)
      || english[0]
      || null;
  }
}
